import { useQuery } from '@tanstack/react-query'
import { APP_URLS, consoleQueries, getApiErrorMessage } from '@milliswift/api-client'
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  PageSkeleton,
  PreviewDataBadge,
  SectionHeader,
} from '@milliswift/ui'
import { Database, ExternalLink, HardDrive, Rows3 } from 'lucide-react'

const formatBytes = (bytes: number): string => {
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(1)} TB`
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`
  return `${bytes} B`
}

const formatRows = (rows: number): string => {
  if (rows >= 1e9) return `${(rows / 1e9).toFixed(1)}B`
  if (rows >= 1e6) return `${(rows / 1e6).toFixed(1)}M`
  if (rows >= 1e3) return `${(rows / 1e3).toFixed(1)}K`
  return String(rows)
}

/**
 * Databases overview.
 *
 * ⚠️  Preview data. GET /api/v1/databases is not implemented on the platform
 * yet (see docs/PLATFORM_API_GAPS.md) — the badge marks every figure below as
 * fixture data until it is.
 */
export default function Databases() {
  const databases = useQuery(consoleQueries.databases())

  if (databases.isPending) return <PageSkeleton />

  const list = databases.data ?? []

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Databases"
        description="Your analytical databases across regions."
        action={<PreviewDataBadge />}
      />

      <Callout tone="info">
        Creating and deleting databases from this page arrives with the console update. Today
        the list is read-only preview data.
      </Callout>

      {databases.isError ? (
        <Callout tone="danger" title="Could not load databases">
          {getApiErrorMessage(databases.error)}
        </Callout>
      ) : list.length === 0 ? (
        <EmptyState
          icon={<Database />}
          title="No databases"
          description="Databases you create will appear here with their size and status."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((db) => (
            <div
              key={db.database_id}
              className="space-y-4 rounded-card border border-line bg-surface p-5 shadow-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-mono text-sm font-semibold text-foreground">{db.name}</h3>
                  <p className="mt-0.5 text-caption text-muted">{db.region}</p>
                </div>
                <Badge
                  tone={db.status === 'active' ? 'success' : db.status === 'provisioning' ? 'warning' : 'neutral'}
                  dot
                >
                  {db.status}
                </Badge>
              </div>

              <dl className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Rows3 className="h-4 w-4 shrink-0 text-subtle" aria-hidden />
                  <div>
                    <dt className="sr-only">Rows</dt>
                    <dd className="font-mono text-sm text-foreground">{formatRows(db.row_count)}</dd>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4 shrink-0 text-subtle" aria-hidden />
                  <div>
                    <dt className="sr-only">Size</dt>
                    <dd className="font-mono text-sm text-foreground">{formatBytes(db.size_bytes)}</dd>
                  </div>
                </div>
              </dl>

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                rightIcon={<ExternalLink className="h-3.5 w-3.5" />}
                onClick={() =>
                  (window.location.href = `${APP_URLS.platform}/tables?db=${db.database_id}`)
                }
              >
                Open in console
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
